import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AmcLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  AmcNtbBlendedLtvRow,
  AmcNtbCohortLtvRow,
  AmcNtbDeliveryMetricsRow,
  AmcNtbLtvResponse
} from '../amc-ntb-ltv-types';

const sanitizeDeliveryMetricsRow = (row: AmcNtbDeliveryMetricsRow): AmcNtbDeliveryMetricsRow => ({
  ...row,
  saSpend: Number(row.saSpend),
  dspSpend: Number(row.dspSpend),
  totalSpend: Number(row.totalSpend),
  totalOrders: Number(row.totalOrders),
  ntbOrders: Number(row.ntbOrders),
  totalCustomers: Number(row.totalCustomers),
  ntbCustomers: Number(row.ntbCustomers),
  totalProductSales: Number(row.totalProductSales),
  ntbTotalProductSales: Number(row.ntbTotalProductSales)
});

const sanitizeBlendedLtvRow = (row: AmcNtbBlendedLtvRow): AmcNtbBlendedLtvRow => ({
  ...row,
  monthOffset: Number(row.monthOffset),
  cohortCountAtOffset: Number(row.cohortCountAtOffset),
  monthOffsetAov: Number(row.monthOffsetAov),
  monthOffsetCumulativeLtv: Number(row.monthOffsetCumulativeLtv),
  totalMonthOffsetSales: Number(row.totalMonthOffsetSales),
  totalMonthOffsetOrders: Number(row.totalMonthOffsetOrders),
  totalMonthOffsetNtbCustomers: Number(row.totalMonthOffsetNtbCustomers),
  totalMonthOffsetSnsSales: Number(row.totalMonthOffsetSnsSales),
  totalMonthOffsetSnsCustomers: Number(row.totalMonthOffsetSnsCustomers),
  totalMonthOffsetSnsNewSubscriptions: Number(row.totalMonthOffsetSnsNewSubscriptions),
  totalMonthOffsetSnsFulfillments: Number(row.totalMonthOffsetSnsFulfillments)
});

const sanitizeCohortLtvRow = (row: AmcNtbCohortLtvRow): AmcNtbCohortLtvRow => ({
  ...row,
  monthOffset: Number(row.monthOffset),
  cumulativeLtv: Number(row.cumulativeLtv)
});

// `/getNtbLtvReport` is a placeholder endpoint name — bpa_delivery_metrics_ltv_report_sql.sql,
// bpa_blended_ltv_report_sql.sql, and bpa_cohort_ltv_report_sql.sql (see amc-ntb-ltv-types.ts)
// are finalized, but no bpm-lambdas route runs them yet. Update this once that endpoint exists;
// the shape in AmcNtbLtvResponse assumes it camelCases each query's columns and combines all
// three into one response, matching every other AMC report endpoint's convention.
const fetchAmcNtbLtvData = async (amcInstanceId: string): Promise<AmcNtbLtvResponse> => {
  const response = await AmcLambdaFetch('/getNtbLtvReport', { params: { amcInstanceId } });
  const json = await awaitJSON<AmcNtbLtvResponse>(response);
  return {
    deliveryMetrics: json.deliveryMetrics.map(sanitizeDeliveryMetricsRow),
    blendedLtv: json.blendedLtv.map(sanitizeBlendedLtvRow),
    cohortLtv: json.cohortLtv.map(sanitizeCohortLtvRow)
  };
};

export const useAmcNtbLtvData = (
  amcInstanceId: string | undefined
): ReturnType<typeof useQuery<AmcNtbLtvResponse>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['amcNtbLtvData', amcInstanceId],
    queryFn: () => {
      if (!amcInstanceId) throw new Error('Missing AMC instance id');
      return fetchAmcNtbLtvData(amcInstanceId);
    },
    enabled: Boolean(amcInstanceId),
    placeholderData: keepPreviousData
  });
